/**
 * Drag the admin tabs into the order you want them, within a category or across to another.
 *
 * Nothing is saved until Save is pressed, so a half-finished rearrangement can be abandoned.
 * The same drag pattern as the Q&A questions: native HTML5 drag, with the drop target lit.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { GripVertical, Loader2, RotateCcw } from 'lucide-react';
import { arrangeTabs } from '@/pages/admin/adminTabs';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function TabOrderDialog({ open, onOpenChange, savedOrder, onSaved, ready = true }) {
  const [draft, setDraft] = useState(() => arrangeTabs(savedOrder));
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const dragFrom = useRef(null);

  // Re-seed every time it opens, so it shows what is actually on screen rather than a stale
  // draft or the default order from before the saved one had loaded.
  useEffect(() => {
    if (!open) return;
    setDraft(arrangeTabs(savedOrder));
    setDragOver(null);
    dragFrom.current = null;
  }, [open, savedOrder]);

  const handleOpenChange = (next) => {
    setDragOver(null);
    dragFrom.current = null;
    onOpenChange(next);
  };

  const move = (toCategory, toIndex) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setDragOver(null);
    if (!from) return;
    if (from.category === toCategory && from.index === toIndex) return;

    setDraft((categories) => {
      const next = categories.map((c) => ({ ...c, tabs: [...c.tabs] }));
      const source = next.find((c) => c.key === from.category);
      const target = next.find((c) => c.key === toCategory);
      const [moved] = source.tabs.splice(from.index, 1);
      if (!moved) return categories;
      const at = toIndex === null || toIndex > target.tabs.length ? target.tabs.length : toIndex;
      target.tabs.splice(at, 0, moved);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const order = Object.fromEntries(draft.map((c) => [c.key, c.tabs.map((t) => t.value)]));
      await axios.put(`${API}/admin/tab-order`, { order }, auth());
      toast.success('Tab order saved');
      onSaved(order);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that order");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    setResetting(true);
    try {
      await axios.delete(`${API}/admin/tab-order`, auth());
      setDraft(arrangeTabs({}));
      toast.success('Back to the original order');
      onSaved({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset the order");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-950 border-white/10"
        data-testid="tab-order-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Reorder the admin tabs</DialogTitle>
          <DialogDescription className="text-gray-400">
            Drag a tab up or down to move it within its group, or across to another group.
            Everyone in Admin sees the order you save here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {draft.map((category) => (
            <div key={category.key} data-testid={`tab-order-group-${category.key}`}>
              <div className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold tracking-wide ${category.color}`}>
                <category.icon className="w-3.5 h-3.5" />
                {category.label}
              </div>

              <div
                className="mt-2 space-y-1"
                onDragOver={(ev) => ev.preventDefault()}
                onDrop={(ev) => { ev.preventDefault(); move(category.key, null); }}
              >
                {category.tabs.length === 0 && (
                  <p className="text-xs text-gray-400 px-3 py-4 rounded-lg border border-dashed border-white/15"
                    data-testid={`tab-order-empty-${category.key}`}>
                    Nothing here — drop a tab in.
                  </p>
                )}

                {category.tabs.map((tab, index) => (
                  <div
                    key={tab.value}
                    draggable
                    onDragStart={() => { dragFrom.current = { category: category.key, index }; }}
                    onDragOver={(ev) => {
                      ev.preventDefault();
                      setDragOver(`${category.key}-${index}`);
                    }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(ev) => { ev.preventDefault(); ev.stopPropagation(); move(category.key, index); }}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      dragOver === `${category.key}-${index}`
                        ? 'border-amber-400/70 bg-amber-400/10 text-white'
                        : 'border-white/10 bg-white/[0.03] text-gray-200 hover:bg-white/[0.06]'
                    }`}
                    data-testid={`tab-order-item-${tab.value}`}
                  >
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-grab shrink-0" />
                    {tab.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={resetToDefault}
            disabled={resetting || saving}
            className="text-gray-300 hover:text-white hover:bg-white/10 mr-auto"
            data-testid="tab-order-reset"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Reset to default
          </Button>
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={saving}
            className="text-gray-300 hover:text-white hover:bg-white/10"
            data-testid="tab-order-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || !ready}
            title={ready ? undefined : 'Still reading the current order'}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="tab-order-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
